import moment from "cc-frontend/utils/extended-moment";
import QRCodeStyling from "qr-code-styling";

export function createShareLink(
  pathname: string,
  /** The base URLSearchParams to use (will be processed per other options). */
  baseUrlParams: URLSearchParams,
  options: { hideNav?: boolean; dateString?: string | null; courseTitle?: string | null } = {}
): string {
  const perspective = perspectiveFromPathname(pathname)

  let dateString = options.dateString ?? null
  if (options.hideNav && !options.dateString && ["day", "week", "month"].includes(perspective)) {
    // We need a dateString for hideNav, but none was provided.
    // This means that the user is looking at the current week -> use today's date.
    dateString = moment().format("YYYY-MM-DD")
  }

  let queryString = ""
  if (perspective !== "lessons") {
    const urlParams = new URLSearchParams(baseUrlParams)
    if (options.hideNav) {
      urlParams.set("hideNav", "true")
    } else {
      urlParams.delete("hideNav")
    }
    if (dateString) {
      urlParams.set("date", dateString)
    } else {
      urlParams.delete("date")
    }
    // URL params that we never want to keep.
    urlParams.delete("openedModals")

    if (urlParams.size > 0) queryString = "?" + urlParams.toString()
  }

  return `${window.location.protocol}//${window.location.host}${pathname}${queryString}`
}

/**
 * A user-visible description of what the share link shows when hideNav is true.
 */
export function describeShareLink(
  pathname: string,
  options: { dateString?: string | null; courseTitle?: string | null } = {}
): string {
  const date = moment(options.dateString || undefined)
  switch (perspectiveFromPathname(pathname)) {
    case "day":
      return date.format("dddd, MMMM DD, YYYY")
    case "week": {
      const startM = date.clone().startOf("isoWeek")
      const endM = date.clone().startOf("isoWeek").add(4, "days")
      return startM.month() === endM.month()
        ? startM.format("MMMM D") + " - " + endM.format("D, YYYY")
        : startM.format("MMM. D") + " - " + endM.format("MMM. D, YYYY")
    }
    case "month":
      return date.format("MMMM YYYY")
    case "timeline":
      return "the Unit Timeline"
    case "outline":
      return "the Planbook Outline"
    case "standards-tracker":
      return "the Standards Tracker"
    case "lessons":
      return `${options.courseTitle!} on ${date.format("dddd, MMMM DD, YYYY")}`
    default:
      return "the current page"
  }
}

export function createQrCode(url: string): QRCodeStyling {
  return new QRCodeStyling({
    // Use default width/height here (300); caller can downscale with CSS.
    type: "svg",
    data: url,
    qrOptions: {
      errorCorrectionLevel: "L",
    },
    margin: 2,
    image: "/assets/images/svg/qr-code-logo.svg",
    imageOptions: {
      imageSize: 0.8,
      margin: 5,
    },
  })
}

export function perspectiveFromPathname(pathname: string): string {
  // A single lesson is /lessons/<cardStackId>, so the last segment is the id, not the perspective.
  if (pathname.startsWith("/lessons/")) return "lessons"
  return pathname.slice(pathname.lastIndexOf("/") + 1)
}
